import { useState } from 'react';
import {
  Modal,
  Stack,
  TextInput,
  Textarea,
  Button,
  Radio,
  Group,
  Text,
  Anchor,
  SimpleGrid,
  Box,
} from '@mantine/core';
import { IconFile, IconSearch, IconTemplate } from '@tabler/icons-react';
import { Link, useNavigate } from 'react-router-dom';
import { useFlowMutations } from '../hooks/useFlowMutations';
import { useTemplates } from '../hooks/useTemplates';
import { flowsApi, type Flow } from '@/services/flows.api';
import { starterFlowDsl } from '@/constants/starterFlowDsl';

interface CreateFlowModalProps {
  opened: boolean;
  onClose: () => void;
}

type StartFrom = 'blank' | 'template';

/** How many templates the picker lists before search takes over. */
const SHOWN_TEMPLATES = 5;

function usageLabel(count = 0) {
  if (count === 0) return 'Not used yet';
  return count === 1 ? 'Used once' : `Used ${count} times`;
}

/**
 * Create a flow, blank or from a template. The first choice is only "blank or
 * template"; picking a template comes second, so the two never read as one
 * list. Templates come from the API most used first.
 */
export function CreateFlowModal({ opened, onClose }: CreateFlowModalProps) {
  const navigate = useNavigate();
  const [startFrom, setStartFrom] = useState<StartFrom>('blank');
  const [templateId, setTemplateId] = useState<string | null>(null);
  const [name, setName] = useState('');
  // Until the user types a name, picking a template fills it in.
  const [nameTouched, setNameTouched] = useState(false);
  const [description, setDescription] = useState('');
  const [search, setSearch] = useState('');
  const [error, setError] = useState<string | null>(null);
  const { createMutation } = useFlowMutations();
  const { templates, startFromTemplate } = useTemplates(opened);

  const query = search.trim().toLowerCase();
  const shownTemplates = query
    ? templates.filter((t) => t.name.toLowerCase().includes(query) || t.description?.toLowerCase().includes(query))
    : templates.slice(0, SHOWN_TEMPLATES);
  const fromTemplate = startFrom === 'template';
  const pending = createMutation.isPending || startFromTemplate.isPending;
  const canCreate = !!name.trim() && !pending && (!fromTemplate || !!templateId);

  const chooseStart = (value: string) => {
    setStartFrom(value as StartFrom);
    setError(null);
    if (value === 'blank') {
      setTemplateId(null);
      if (!nameTouched) setName('');
    }
  };

  const chooseTemplate = (id: string) => {
    setTemplateId(id);
    if (!nameTouched) setName(templates.find((t) => t.id === id)?.name ?? '');
  };

  const reset = () => {
    setStartFrom('blank');
    setTemplateId(null);
    setName('');
    setNameTouched(false);
    setDescription('');
    setSearch('');
    setError(null);
  };

  const handleSubmit = async () => {
    if (!canCreate) return;
    const trimmed = name.trim();
    setError(null);
    try {
      let flow: Flow;
      if (fromTemplate && templateId) {
        // An empty description keeps the template's own.
        flow = await startFromTemplate.mutateAsync({
          id: templateId,
          name: trimmed,
          description: description.trim() || undefined,
        });
      } else {
        flow = await createMutation.mutateAsync({ name: trimmed, description });
        await flowsApi.createVersion(flow.id, starterFlowDsl(flow.id, flow.name));
      }
      reset();
      onClose();
      navigate(`/flows/${flow.id}`);
    } catch (err: any) {
      setError(err?.response?.data?.message ?? 'Failed to create flow. Please try again.');
    }
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <Modal opened={opened} onClose={handleClose} title="Create New Flow" size="lg">
      <Stack>
        <Radio.Group value={startFrom} onChange={chooseStart} label="Start from">
          <SimpleGrid cols={2} spacing="sm" mt={6}>
            <Radio.Card value="blank" radius="md" p="md" aria-label="Blank flow">
              <Group wrap="nowrap" align="flex-start" gap="sm">
                <Radio.Indicator mt={2} />
                <Box>
                  <Group gap={6}>
                    <IconFile size={16} />
                    <Text size="sm" fw={600}>Blank flow</Text>
                  </Group>
                  <Text size="xs" c="dimmed" mt={2}>Start with an empty first page.</Text>
                </Box>
              </Group>
            </Radio.Card>
            <Radio.Card
              value="template"
              radius="md"
              p="md"
              aria-label="From a template"
              disabled={templates.length === 0}
              style={templates.length === 0 ? { opacity: 0.6, cursor: 'not-allowed' } : undefined}
            >
              <Group wrap="nowrap" align="flex-start" gap="sm">
                <Radio.Indicator mt={2} disabled={templates.length === 0} />
                <Box>
                  <Group gap={6}>
                    <IconTemplate size={16} />
                    <Text size="sm" fw={600}>From a template</Text>
                  </Group>
                  <Text size="xs" c="dimmed" mt={2}>
                    {templates.length === 0
                      ? 'No templates yet. Save a flow as one from its ⋯ menu.'
                      : 'Copy one of your templates.'}
                  </Text>
                </Box>
              </Group>
            </Radio.Card>
          </SimpleGrid>
        </Radio.Group>

        {fromTemplate && (
          <Radio.Group
            value={templateId}
            onChange={chooseTemplate}
            label="Choose a template"
            description="Most used first"
          >
            <Stack gap={6} mt={6}>
              {templates.length > SHOWN_TEMPLATES && (
                <TextInput
                  placeholder="Search all templates"
                  aria-label="Search templates"
                  leftSection={<IconSearch size={14} />}
                  value={search}
                  onChange={(e) => setSearch(e.currentTarget.value)}
                  size="xs"
                />
              )}
              {shownTemplates.map((template) => (
                <Radio.Card
                  key={template.id}
                  value={template.id}
                  radius="md"
                  p="sm"
                  aria-label={`Template ${template.name}`}
                >
                  <Group wrap="nowrap" gap="sm" align="flex-start">
                    <Radio.Indicator mt={2} />
                    <Stack gap={0} style={{ flex: 1, minWidth: 0 }}>
                      <Group justify="space-between" wrap="nowrap">
                        <Text size="sm" fw={500} lineClamp={1}>{template.name}</Text>
                        <Text size="xs" c="dimmed" style={{ whiteSpace: 'nowrap' }}>
                          {usageLabel(template.templateUseCount)}
                        </Text>
                      </Group>
                      {template.description && (
                        <Text size="xs" c="dimmed" lineClamp={1}>{template.description}</Text>
                      )}
                    </Stack>
                  </Group>
                </Radio.Card>
              ))}
              {shownTemplates.length === 0 && (
                <Text size="xs" c="dimmed" px="sm">No templates match "{search}".</Text>
              )}
              <Anchor component={Link} to="/library/templates" size="xs" onClick={handleClose}>
                View all templates →
              </Anchor>
            </Stack>
          </Radio.Group>
        )}

        <TextInput
          label="Flow Name"
          placeholder="My Flow"
          value={name}
          onChange={(e) => {
            setName(e.currentTarget.value);
            setNameTouched(true);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') handleSubmit();
          }}
        />
        <Textarea
          label="Description (optional)"
          placeholder={fromTemplate ? "Leave empty to keep the template's description" : 'Describe what this flow does...'}
          value={description}
          onChange={(e) => setDescription(e.currentTarget.value)}
          rows={3}
        />
        {error && <Text size="sm" c="red">{error}</Text>}
        <Button onClick={handleSubmit} disabled={!canCreate} loading={pending} style={{ backgroundColor: '#415A91' }}>
          Create
        </Button>
      </Stack>
    </Modal>
  );
}
